import { keyframes } from '@mui/material/styles';
import { colors } from '../../../../theme';

const animations = {
  rotate: keyframes`
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  `,
  glow: keyframes`
    0% {
      box-shadow: 0 0 5px var(--ui-accent);
    }
    50% {
      box-shadow: 0 0 20px var(--ui-accent);
    }
    100% {
      box-shadow: 0 0 5px var(--ui-accent);
    }
  `
};

export const getDashboardStyles = (theme, activeSection) => {
  const mode = theme?.palette?.mode || 'light';
  const actualMode = theme?.palette?.actualMode || mode;

  // Usar los colores del modo actual (wallpaper, light, o dark)
  let themeColors;
  if (actualMode === 'wallpaper') {
    themeColors = colors.wallpaper;
  } else if (mode === 'light') {
    themeColors = colors.light;
  } else {
    themeColors = colors.dark;
  }

  // Verificamos si la sección activa es "ups", "profile" o "fin"
  const isUpsActive = activeSection === 'ups';
  const isProfileActive = activeSection === 'profile';
  const isFinActive = activeSection === 'fin';

  // Helper para reemplazar bgPrimaryBase por #FFFFFF (claro) o #000000 (oscuro) si está en AGTUps, Profile o Fin
  const getSpecialSectionBackgroundColor = (defaultColor) => {
    if (isUpsActive || isProfileActive || isFinActive) {
      return mode === 'light' ? '#FFFFFF' : '#000000';
    }
    return defaultColor;
  };

  return {
    fixedContainer: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      transition: 'background-image 0.5s ease-in-out, background-color 0.5s ease-in-out',
      backgroundColor: themeColors.bg.primaryElevated,
      color: themeColors.label.primary
    },

    windowBar: {
      width: '100%',
      height: '100%',
      backgroundColor: 'transparent',
      // Sin blur en modo wallpaper para ver la imagen claramente
      backdropFilter: actualMode === 'wallpaper' ? 'none' : 'blur(40px)',
      color: themeColors.label.primary,
      fontFamily: "'Inter', sans-serif",
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: themeColors.ui.backgroundBlur
    },

    topBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0.5, 2),
      // Transparente en wallpaper para ver la imagen
      backgroundColor: actualMode === 'wallpaper' ? 'transparent' : themeColors.bg.primaryElevated,
      height: '48px', // Keep this height consistent
      position: 'sticky',
      top: 0,
      zIndex: 1100,
      marginBottom: 0, // Ensure no margin below
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.25, 1),
        height: '44px' // Slightly shorter on mobile
      }
    },

    navContainer: {
      display: 'flex',
      justifyContent: 'center',
      flex: 1,
      [theme.breakpoints.down('sm')]: {
        maxWidth: 'calc(100% - 80px)',
        margin: '0 auto'
      }
    },

    contentArea: {
      flexGrow: 1,
      overflow: 'hidden',
      padding: 0, // Remove all padding
      // Transparente en wallpaper, sólido en otros modos
      backgroundColor: actualMode === 'wallpaper'
        ? 'transparent'
        : getSpecialSectionBackgroundColor(themeColors.grouped.bgPrimaryBase),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: activeSection === 'home' ? 'center' : 'flex-start',
      [theme.breakpoints.down('sm')]: {
        padding: 0,
        marginTop: 0, // Ensure no margin at top
        // En modo wallpaper siempre transparente, incluso en móvil
        backgroundColor: actualMode === 'wallpaper'
          ? 'transparent'
          : ((isUpsActive || isProfileActive || isFinActive)
            ? (mode === 'light' ? '#FFFFFF' : '#000000')
            : 'transparent'),
        justifyContent: 'flex-start'
      }
    },

    navigationContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(1),
      // Semi-transparente en wallpaper para mejor visibilidad
      backgroundColor: actualMode === 'wallpaper'
        ? 'rgba(0, 0, 0, 0.4)'
        : (mode === 'light' ? '#E5E5EA' : themeColors.bg.primaryElevated),
      backdropFilter: actualMode === 'wallpaper' ? 'none' : 'blur(10px)',
      borderRadius: '50px',
      margin: '0 auto',
      width: 'fit-content',
      position: 'relative',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.5),
        width: '100%'
      }
    },

    slidingBackground: {
      position: 'absolute',
      top: 5,
      left: 0,
      height: 'calc(100% - 10px)',
      borderRadius: '50px',
      backgroundColor: themeColors.fill.tertiary,
      transition: 'transform 0.3s ease, width 0.3s ease'
    },

    navButton: (isActive) => ({
      color: isActive ? themeColors.ui.accent : themeColors.label.primary,
      margin: theme.spacing(0, 0.5),
      textTransform: 'capitalize',
      borderRadius: '50px',
      zIndex: 1,
      padding: theme.spacing(0.5, 2),
      backgroundColor: 'transparent',
      boxShadow: 'none',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        color: themeColors.ui.accent
      },
      '&:active': {
        backgroundColor: 'transparent',
        boxShadow: 'none'
      },
      '&:focus': {
        backgroundColor: 'transparent',
        boxShadow: 'none'
      },
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.5, 1),
        margin: theme.spacing(0, 0.25),
        fontSize: '0.8rem'
      }
    }),

    animatedIconButton: {
      color: themeColors.label.primary,
      transition: 'all 0.3s ease',
      '&:hover': {
        color: themeColors.ui.accent
      },
      '&.rotate': {
        animation: `${animations.rotate} 0.5s linear`
      },
      '&.glow': {
        animation: `${animations.glow} 1s ease-in-out`
      }
    },

    avatar: (isMobile) => ({
      cursor: 'pointer',
      width: isMobile ? 28 : 32,  // Reduced from 32, 40
      height: isMobile ? 28 : 32  // Reduced from 32, 40
    }),
     
    backgroundButton: (isMobile) => ({
      color: themeColors.label.primary,
      width: isMobile ? 28 : 32,  // Reduced from 32, 40
      height: isMobile ? 28 : 32,  // Reduced from 32, 40
      padding: isMobile ? '4px' : '6px',  // Reduced from 4px, 8px
      marginRight: isMobile ? '4px' : '8px',
      '&:hover': {
        color: themeColors.ui.accent
      }
    }),

    contentBox: {
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: '4px', // Reduced from 10px to 4px
      // Transparente en wallpaper, sólido en otros modos
      backgroundColor: actualMode === 'wallpaper'
        ? 'transparent'
        : getSpecialSectionBackgroundColor(themeColors.grouped.bgPrimaryBase),
      [theme.breakpoints.down('sm')]: {
        margin: 0,
        paddingTop: 0, // No padding on mobile
        // En modo wallpaper siempre transparente, incluso en móvil
        backgroundColor: actualMode === 'wallpaper'
          ? 'transparent'
          : ((isUpsActive || isProfileActive || isFinActive)
            ? (mode === 'light' ? '#FFFFFF' : '#000000')
            : 'transparent')
      }
    },

    chatBox: {
      flexGrow: 1,
      overflowY: 'auto',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      // Transparente en wallpaper, sólido en otros modos
      backgroundColor: actualMode === 'wallpaper'
        ? 'transparent'
        : getSpecialSectionBackgroundColor(themeColors.grouped.bgPrimaryBase),
      [theme.breakpoints.down('sm')]: {
        margin: 0,
        marginTop: '-1px'
      }
    },

    homeBox: {
      flexGrow: 1,
      overflow: 'hidden',
      width: '100%',
      height: '100%', // Asegurar que ocupa toda la altura
      display: 'flex', // Añadir flex
      flexDirection: 'column', // Añadir flex direction
      justifyContent: 'center', // Centrar verticalmente
      // Transparente en wallpaper, sólido en otros modos
      backgroundColor: actualMode === 'wallpaper'
        ? 'transparent'
        : getSpecialSectionBackgroundColor(themeColors.grouped.bgPrimaryBase),
      paddingBottom: '60px',
      [theme.breakpoints.down('sm')]: {
        backgroundColor: 'transparent',
        justifyContent: 'flex-start', // En móvil, empezar desde arriba
        paddingBottom: '50px'
      }
    },

    snackbarAlert: {
      borderRadius: 28,
      backgroundColor: mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(20px)',
      boxShadow: mode === 'dark'
        ? '0px 1px 3px rgba(0, 0, 0, 0.5)'
        : '0px 8px 32px rgba(0, 0, 0, 0.08)',
      padding: theme.spacing(2, 3),
      minWidth: '300px',
      border: 'none',
      '& .MuiAlert-icon': {
        color: mode === 'dark' ? '#fff' : '#0071e3',
        opacity: 0.9
      },
      '& .MuiAlert-message': {
        padding: theme.spacing(1, 0),
        fontSize: '0.95rem',
        color: mode === 'dark' ? '#fff' : '#000'
      },
      '& .MuiAlert-action': {
        padding: theme.spacing(0, 0, 0, 2),
        '& .MuiIconButton-root': {
          padding: 8,
          borderRadius: 20,
          color: mode === 'dark' ? '#fff' : '#0071e3',
          '&:hover': {
            backgroundColor: mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 113, 227, 0.1)'
          }
        }
      }
    },

    snackbarContainer: {
      '& .MuiSnackbarContent-root': {
        backgroundColor: 'transparent',
        padding: 0,
        minWidth: 'auto',
        boxShadow: 'none'
      }
    }
  };
};

export default getDashboardStyles;
